import { useEffect, useState } from "react";
import { buildConnectUrl, connectToDeriv, AFFILIATE_URL, api } from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { Bot, Zap, Shield, LineChart, Dices, Rocket, ArrowUpRight, Sparkles, Infinity as InfinityIcon, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LobbyTiles } from "@/components/casino/LobbyTiles";
import { Avatar } from "@/components/casino/Avatar";
import { LOBBY_ART, GAME_COVERS } from "@/lib/casinoArt";

const games = [
  { key: "penalty", title: "Penalty Shootout", tag: "×1.95", desc: "Beat the keeper — left or right." },
  { key: "wheel", title: "Lucky Wheel", tag: "×1.95", desc: "Spin up or spin down." },
  { key: "dice", title: "Dice Duel", tag: "×1.95", desc: "Even or odd before it settles." },
  { key: "flip", title: "Red / Black", tag: "×1.95", desc: "Call the card colour." },
  { key: "treasure", title: "Treasure Duel", tag: "×1.95", desc: "High chest or low chest." },
  { key: "hoop", title: "Hoop Shot", tag: "×1.95", desc: "Bank it up or stuff it down." },
];

const strategies = [
  { name: "Martingale", desc: "Double after each loss, reset on win.", icon: InfinityIcon },
  { name: "D'Alembert", desc: "Progressive +/- 1 unit after each round.", icon: LineChart },
  { name: "Oscar's Grind", desc: "Target one-unit profit per cycle.", icon: Rocket },
  { name: "Reverse Martingale", desc: "Press winners, protect capital.", icon: Zap },
  { name: "Accumulator", desc: "Compound each winning tick (+10%).", icon: Rocket },
];

const tickerPairs = [
  "EUR/USD 1.1191", "GBP/USD 1.3244", "USD/JPY 158.31", "Gold 4177.31", "Silver 59.94",
  "BTC/USD 83206.27", "ETH/USD 2501.32", "AUD/USD 0.6977", "USD/CAD 1.4235", "Vol 100 646.23",
];

export default function Landing() {
  const [winners, setWinners] = useState([]);
  const [board, setBoard] = useState([]);
  const [isShowcase, setIsShowcase] = useState(false);
  useEffect(() => {
    api.get("/arcade/leaderboard", { params: { showcase: true } }).then((r) => {
      setBoard(r.data?.rows || []);
      setIsShowcase(!!r.data?.showcase);
    }).catch(() => {});
  }, []);
  const [winnerIdx, setWinnerIdx] = useState(0);
  useEffect(() => {
    const pull = () => api.get("/arcade/live-winners", { params: { limit: 12 } }).then((r) => setWinners(r.data || [])).catch(() => {});
    pull();
    const p = setInterval(pull, 20000);
    const id = setInterval(() => setWinnerIdx((i) => i + 1), 2200);
    return () => { clearInterval(p); clearInterval(id); };
  }, []);
  const shown = winners.length ? Array.from({ length: Math.min(3, winners.length) }, (_, k) => winners[(winnerIdx + k) % winners.length]) : [];

  return (
    <div className="relative">
      <SiteNav />

      {/* Ticker */}
      <div className="border-y border-white/5 bg-black/40 py-3 ticker-mask overflow-hidden">
        <div className="marquee-track flex gap-10 font-mono-naevo text-xs text-white/70">
          {[...tickerPairs, ...tickerPairs, ...tickerPairs].map((p, i) => (
            <span key={i} className="whitespace-nowrap">
              <span className="text-magenta">●</span> {p}
            </span>
          ))}
        </div>
      </div>

      {/* Casino hero */}
      <section className="relative overflow-hidden" data-testid="casino-hero">
        <img src={LOBBY_ART.hero} alt="Naevo casino floor" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#070504] to-transparent" />
        <div className="bulb-row absolute top-0 left-0 right-0" />
        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-28 sm:pt-28 sm:pb-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-black/50 px-3 py-1 text-xs text-gold">
              <Sparkles className="h-3 w-3" /> Official Deriv partner · Live 24/7
            </div>
            <h1 className="font-casino text-gold-grad mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              The house of<br />smart bets.
            </h1>
            <p className="mt-6 text-white/75 max-w-xl text-base sm:text-lg">
              Six live casino tables, a real trading floor and an always-on bot — every round is a real Deriv contract on your own account. Practice on demo, cash out on real.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={buildConnectUrl()} onClick={(e) => { e.preventDefault(); connectToDeriv(); }} data-testid="hero-connect-deriv" className="btn-gold rounded-full px-7 py-3.5 flex items-center gap-2">
                Enter the casino <ArrowUpRight className="h-4 w-4" />
              </a>
              <a href={AFFILIATE_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-signup-cta" className="btn-velvet rounded-full px-7 py-3.5">
                Get $10,000 free demo
              </a>
              <a href="/dashboard" data-testid="hero-open-desk" className="btn-ghost rounded-full px-6 py-3.5 text-sm">Lobby →</a>
            </div>
          </motion.div>
          <div className="mt-12 flex flex-wrap gap-3" data-testid="hero-winners">
            <AnimatePresence initial={false} mode="popLayout">
              {shown.map((w, i) => (
                <motion.div key={`${w.handle}-${winnerIdx + i}`} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
                  className="rounded-full border border-gold/30 bg-black/60 backdrop-blur px-4 py-2 text-sm flex items-center gap-3">
                  <span className="h-2 w-2 rounded-full bg-[color:var(--naevo-success)] animate-pulse" />
                  <Avatar id={w.avatar} size={22} />
                  <span className="text-white/90 font-mono-naevo">{w.handle}</span>
                  <span className="text-white/50 text-xs">{w.game}</span>
                  <span className="font-mono-naevo text-[color:var(--naevo-success)]">+${Number(w.profit).toFixed(2)}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Lobby */}
      <section className="px-6 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="text-xs uppercase tracking-[0.35em] text-gold/70">The lobby</div>
          <h2 className="font-casino text-gold-grad text-3xl sm:text-4xl mt-2 mb-8">Pick your room</h2>
          <LobbyTiles />
        </div>
      </section>

      {/* Tables */}
      <section className="px-6 py-16 sm:py-20 border-t border-gold/10">
        <div className="mx-auto max-w-7xl">
          <div className="text-xs uppercase tracking-[0.35em] text-gold/70">Casino tables</div>
          <h2 className="font-casino text-gold-grad text-3xl sm:text-4xl mt-2">Six games. One tap each.</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8">
            {games.map((g) => (
              <a key={g.key} href="/arcade" data-testid={`arcade-card-${g.key}`} className="group casino-tile rounded-2xl overflow-hidden relative h-48 sm:h-64">
                <img src={GAME_COVERS[g.key]} alt={g.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 p-4 sm:p-5">
                  <div className="marquee-badge inline-block">{g.tag}</div>
                  <div className="font-casino text-gold-grad text-xl sm:text-2xl mt-2">{g.title}</div>
                  <div className="text-xs sm:text-sm text-white/70 mt-1 hidden sm:block">{g.desc}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="px-6 py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl">
          <div className="text-xs uppercase tracking-widest text-white/40">03 — Why Naevo</div>
          <h2 className="font-casino text-gold-grad text-3xl sm:text-4xl mt-2 max-w-3xl">
            Built for beginners. <br/><span className="italic text-lavender">Engineered</span> for pros.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { icon: Shield, title: "OAuth-secure", desc: "We never see your Deriv password. Connect via Deriv's official OAuth handshake." },
              { icon: Zap, title: "Sub-second execution", desc: "Direct WebSocket pipe to Deriv. Quotes refresh in real time, trades execute instantly." },
              { icon: Bot, title: "24/7 preset bots", desc: "Server-side bots that keep running even with your phone off. Stop any time." },
              { icon: Dices, title: "Real-money arcade", desc: "Six gamified contracts with built-in loss limits and auto cash-out." },
              { icon: LineChart, title: "Live signals", desc: "Volatility, forex, metals and crypto streams right under your trades." },
              { icon: Lock, title: "Smart profit lock", desc: "Winning trades ladder into smaller contracts to bank partial profit automatically." },
            ].map((f) => (
              <div key={f.title} className="card-naevo rounded-xl p-6">
                <div className="h-10 w-10 rounded-md bg-lavender/10 border border-lavender/30 flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-lavender" />
                </div>
                <div className="font-display text-xl mt-4">{f.title}</div>
                <p className="text-sm text-white/60 mt-2">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="px-6 py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl">
          <div className="text-xs uppercase tracking-widest text-white/40">04 — Leaderboard</div>
          <h2 className="font-casino text-gold-grad text-3xl sm:text-4xl mt-2 max-w-3xl">
            This week's <span className="italic text-magenta">top players</span>.
          </h2>
          <p className="mt-3 text-white/60 max-w-2xl">
            {isShowcase ? "Set your username in the Lobby and climb the ranks this week." : "Only players on a winning run — one loss and they drop off until they're back in profit."}
          </p>
          <div className="mt-10 rounded-2xl border border-white/10 bg-[#0C0914] overflow-hidden">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-widest text-white/40 font-mono-naevo">
              <div className="col-span-1">Rank</div>
              <div className="col-span-5">Player</div>
              <div className="col-span-3 text-right">Wins / Rounds</div>
              <div className="col-span-3 text-right">Profit</div>
            </div>
            {board.length === 0 && (
              <div className="px-5 py-10 text-center text-white/45 text-sm" data-testid="leaderboard-empty">No ranked players yet this week — play a round to claim #1.</div>
            )}
            {board.map((r, i) => (
              <motion.div key={r.handle + i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className={`grid grid-cols-12 px-5 py-3 items-center text-sm border-b border-white/5 last:border-0 ${i === 0 ? "bg-gold/5" : ""}`}
                data-testid={`leaderboard-row-${i + 1}`}>
                <div className="col-span-1 font-mono-naevo text-gold/80">#{i + 1}</div>
                <div className="col-span-5 font-mono-naevo text-white/90 flex items-center gap-3"><Avatar id={r.avatar} size={30} />{r.handle}{r.account_type === "demo" && <span className="text-[10px] text-white/40"> · demo</span>}</div>
                <div className="col-span-3 text-right font-mono-naevo text-xs text-white/60">{r.wins} / {r.rounds}</div>
                <div className={`col-span-3 text-right font-mono-naevo ${r.profit >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}`}>{r.profit >= 0 ? "+" : ""}${r.profit.toFixed(2)}</div>
              </motion.div>
            ))}
          </div>
          {isShowcase && <div className="mt-2 text-right text-[10px] uppercase tracking-[0.25em] text-white/35" data-testid="leaderboard-showcase-label">Showcase · sample results</div>}
          <div className="mt-6 text-center flex items-center justify-center gap-3 flex-wrap">
            <a
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leaderboard-cta"
              className="btn-magenta rounded-full px-7 py-3 inline-block font-medium"
            >
              Sign up & climb the ranks
            </a>
            <a
              href={buildConnectUrl()}
              onClick={(e) => { e.preventDefault(); connectToDeriv(); }}
              data-testid="leaderboard-connect-cta"
              className="btn-ghost rounded-full px-7 py-3 inline-block font-medium"
            >
              Already on Deriv? Connect →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ-ish CTA */}
      <section className="px-6 py-24 border-t border-white/5">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="font-casino text-gold-grad text-3xl sm:text-4xl">
            Ready to trade your <span className="italic text-lavender">first</span> contract?
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            New to Deriv? Create a free account in two minutes with a $10,000 demo. Already have one? Open the Naevo Desk and connect in a click.
          </p>
          <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
            <a
              href={AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-signup-cta"
              className="btn-magenta rounded-full px-7 py-3 font-medium"
            >
              Sign up
            </a>
            <a
              href={buildConnectUrl()}
              onClick={(e) => { e.preventDefault(); connectToDeriv(); }}
              data-testid="footer-connect-deriv"
              className="btn-ghost rounded-full px-7 py-3 font-medium"
            >
              Connect my Deriv account →
            </a>
            <a href="/dashboard" className="btn-ghost rounded-full px-7 py-3 font-medium" data-testid="footer-open-desk">
              Open Naevo Desk →
            </a>
          </div>
          <p className="mt-4 text-xs text-white/40">No credit card · Free demo · Real markets</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
